/**
 * Combined export control for the Recoverable Transactions workspace (MOR-738).
 *
 * One "Export" button opens a popover where the practitioner picks the claim
 * scope (all heads by default, or specific claims) and the format (Excel /
 * Word / PDF) — the same interaction as the Bank Transactions export.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { saveAs } from "file-saver";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Table2,
} from "lucide-react";
import { Button } from "@/ui/primitives/button";
import {
  CustomExportDialog,
  datasetsFromContent,
} from "@/components/customExport";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { exportToExcel, exportToWord, exportToPdf } from "@/utils/export";
import {
  buildVoidableTransactionsExport,
  type FlagExportRow,
} from "./buildVoidableTransactionsExport";
import type { FlaggedHeadKey, WorkspaceUI } from "./flaggedShared";

type ExportFormat = "excel" | "word" | "pdf";

const FORMATS: Array<{
  key: ExportFormat;
  label: string;
  ext: string;
  icon: typeof FileText;
}> = [
  { key: "excel", label: "Excel", ext: "xlsx", icon: FileSpreadsheet },
  { key: "word", label: "Word", ext: "docx", icon: FileText },
  { key: "pdf", label: "PDF", ext: "pdf", icon: FileText },
];

export function VoidableTransactionsExportButton({
  jobId,
  workspace,
}: {
  jobId: Id<"jobs">;
  workspace: WorkspaceUI;
}) {
  const [headFilter, setHeadFilter] = useState<string[]>([]);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [customOpen, setCustomOpen] = useState(false);

  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries
        .listFlagsForExport,
      { jobId },
    ),
  );
  const flags = data as FlagExportRow[] | undefined;

  const headOptions = useMemo(
    () =>
      workspace.heads.map((h) => ({
        value: h.headKey,
        label: h.name,
        title: h.provision,
      })),
    [workspace.heads],
  );

  const content = useMemo(
    () =>
      flags
        ? buildVoidableTransactionsExport(
            workspace,
            flags,
            headFilter as FlaggedHeadKey[],
          )
        : null,
    [workspace, flags, headFilter],
  );

  const handleExport = async ({
    key,
    ext,
  }: {
    key: ExportFormat;
    ext: string;
  }) => {
    if (!content) return;
    setBusy(key);
    try {
      const blob =
        key === "excel"
          ? exportToExcel(content)
          : key === "word"
            ? await exportToWord(content)
            : await exportToPdf(content);
      const scope = headFilter.length > 0 ? `_${headFilter.length}_claims` : "";
      saveAs(blob, `Recoverable_Transactions${scope}.${ext}`);
    } catch (err) {
      console.error(`Recoverable transactions export (${key}) failed:`, err);
    } finally {
      setBusy(null);
    }
  };

  const loading = flags === undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={loading}
          title={loading ? "Transactions are still loading" : undefined}
        >
          <Download className="h-3.5 w-3.5" />
          {loading ? "Loading…" : "Export"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="mb-1.5 text-xs font-medium text-foreground">Claims</div>
        <MultiSearchableSelect
          className="w-full"
          value={headFilter}
          onValueChange={setHeadFilter}
          options={headOptions}
          placeholder="All claims"
          searchPlaceholder="Search claims..."
          contentClassName="w-[16rem]"
        />
        <p className="mt-1 text-[11px] text-muted-foreground">
          Leave empty to export all claims.
        </p>
        <div className="mb-1.5 mt-3 text-xs font-medium text-foreground">
          Download as
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {FORMATS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={busy !== null}
              onClick={() => handleExport(f)}
            >
              {busy === f.key ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <f.icon className="h-3.5 w-3.5" />
              )}
              {f.label}
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="mt-1.5 w-full gap-1.5"
          disabled={!content}
          onClick={() => setCustomOpen(true)}
        >
          <Table2 className="h-3.5 w-3.5" />
          Custom export…
        </Button>
      </PopoverContent>
      {/* Sibling of the popover — Radix unmounts popover content on close. */}
      {content && (
        <CustomExportDialog
          open={customOpen}
          onOpenChange={setCustomOpen}
          datasets={datasetsFromContent(content)}
          title={content.title}
        />
      )}
    </Popover>
  );
}
